import { sortOrganisations } from "@/utils/organisationSort";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { SidebarProvider } from "@/components/ui/sidebar";
import { AppSidebar } from "@/components/AppSidebar";
import { FixedPageHeader } from "@/components/ui/fixed-page-header";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Stethoscope, Search, RotateCw } from "lucide-react";
import { toast } from "sonner";
import { useRoleFlags } from "@/hooks/use-role-flags";
import { useUserOrganization } from "@/hooks/use-user-organization";
import {
  ProductionDeviceCard,
  type ProductionDevice,
} from "@/components/devices/ProductionDeviceCard";

const COLUMNS =
  "device_id, device_name, device_serial, device_firmware, device_last_updated, device_status, imagekit, battery, cellular, camera_light_level, ambient_lux, device_description, camera_image_quality, images_allowed, images_current, group_name, group_reference, past_images, camera_config, enable_production_test";

const DeviceDiagnosis = () => {
  const navigate = useNavigate();
  const { role, isAdmin, isSuperuser } = useRoleFlags();
  const { data: ownOrgId } = useUserOrganization();
  const [pickedOrg, setPickedOrg] = useState<string>("");
  const [search, setSearch] = useState("");

  useEffect(() => {
    if (role && !isAdmin && !isSuperuser) navigate("/dashboard");
  }, [role, isAdmin, isSuperuser, navigate]);

  const { data: orgs = [] } = useQuery({
    queryKey: ["diagnosis-orgs"],
    enabled: isSuperuser,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("organizations")
        .select("id, name")
        .order("name");
      if (error) throw error;
      return data || [];
    },
  });

  const orgId = isSuperuser ? pickedOrg || (ownOrgId as string) : (ownOrgId as string);

  const { data: devices = [], isLoading, refetch, isFetching } = useQuery({
    queryKey: ["diagnosis-devices", orgId],
    enabled: !!orgId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("device_details")
        .select(COLUMNS)
        .eq("organization_id", orgId)
        .order("device_last_updated", { ascending: false, nullsFirst: false });
      if (error) throw error;
      return (data || []) as unknown as ProductionDevice[];
    },
  });

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return devices;
    return devices.filter(
      (d) =>
        (d.device_name || "").toLowerCase().includes(q) ||
        (d.device_serial || "").toLowerCase().includes(q) ||
        (d.device_id || "").toLowerCase().includes(q)
    );
  }, [devices, search]);

  return (
    <SidebarProvider>
      <div className="flex min-h-screen w-full bg-background">
        <AppSidebar />
        <main className="flex-1">
          <FixedPageHeader
            title="Device Diagnosis"
            description="Check your cameras' status and history to help diagnose problems"
            icon={Stethoscope}
          />
          <div className="px-4 py-6 md:p-8 space-y-4" style={{ paddingTop: "8.5rem" }}>
            <div className="flex flex-wrap items-center gap-2">
              {isSuperuser && (
                <Select value={orgId || ""} onValueChange={setPickedOrg}>
                  <SelectTrigger className="w-64">
                    <SelectValue placeholder="Choose organisation" />
                  </SelectTrigger>
                  <SelectContent>
                    {sortOrganisations(orgs, o => o.name).map((o) => (
                      <SelectItem key={o.id} value={o.id}>
                        {o.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              <div className="relative w-full max-w-md">
                <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Filter by name or serial…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-8"
                />
              </div>
              <div className="text-sm text-muted-foreground whitespace-nowrap">
                {filtered.length} / {devices.length}
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={async () => {
                  await refetch();
                  toast.success("Updated");
                }}
                disabled={isFetching}
                className="ml-auto"
              >
                <RotateCw className={`h-4 w-4 mr-2 ${isFetching ? "animate-spin" : ""}`} />
                Refresh
              </Button>
            </div>

            {isLoading ? (
              <div className="text-sm text-muted-foreground">Loading devices…</div>
            ) : filtered.length === 0 ? (
              <div className="text-sm text-muted-foreground">No devices found.</div>
            ) : (
              <div className="space-y-3">
                {filtered.map((device) => (
                  <ProductionDeviceCard
                    key={device.device_id}
                    device={device}
                    mode="diagnosis"
                    onChanged={() => refetch()}
                  />
                ))}
              </div>
            )}
          </div>
        </main>
      </div>
    </SidebarProvider>
  );
};

export default DeviceDiagnosis;
